import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { SPOTIFY_CONFIG } from '../config/spotify';

async function checkSpotifyPremiumStatus(accessToken) {
  const response = await fetch('https://api.spotify.com/v1/me', {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch Spotify user info');
  }

  const data = await response.json();
  return data.product === 'premium';
}

async function updatePremiumStatus(userId, isPremium) {
  await setDoc(
    doc(db, 'users', userId),
    { spotifyPremium: isPremium },
    { merge: true }
  );
}

export async function getSpotifyPremiumStatus() {
  if (!auth.currentUser) {
    throw new Error('No user logged in');
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));

    if (userDoc.exists() && userDoc.data().spotifyPremium !== undefined) {
      return userDoc.data().spotifyPremium;
    }
    
    const token = await getValidSpotifyToken();
    const isPremium = await checkSpotifyPremiumStatus(token);
    await updatePremiumStatus(auth.currentUser.uid, isPremium);
    
    return isPremium;
  } catch (error) {
    throw error;
  }
}

export function getSpotifyAuthUrl() {
  const scope = SPOTIFY_CONFIG.SCOPES.join(' ');
  return `https://accounts.spotify.com/authorize?client_id=${SPOTIFY_CONFIG.CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(SPOTIFY_CONFIG.REDIRECT_URI)}&scope=${encodeURIComponent(scope)}`;
}

export async function getSpotifyTokens(code) {
  if (!auth.currentUser) {
    throw new Error('No user logged in');
  }

  const response = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': 'Basic ' + btoa(`${SPOTIFY_CONFIG.CLIENT_ID}:${SPOTIFY_CONFIG.CLIENT_SECRET}`)
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: SPOTIFY_CONFIG.REDIRECT_URI
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error_description || 'Failed to get access token');
  }

  const data = await response.json();
  const tokenData = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: Date.now() + (data.expires_in * 1000),
    scope: data.scope
  };

  const isPremium = await checkSpotifyPremiumStatus(data.access_token);
  
  await Promise.all([
    setDoc(
      doc(db, 'users', auth.currentUser.uid, 'private', 'spotify'),
      tokenData
    ),
    updatePremiumStatus(auth.currentUser.uid, isPremium)
  ]);

  return tokenData;
}

export async function getValidSpotifyToken() {
  if (!auth.currentUser) {
    throw new Error('No user logged in');
  }

  const docRef = doc(db, 'users', auth.currentUser.uid, 'private', 'spotify');
  const tokenDoc = await getDoc(docRef);

  if (!tokenDoc.exists()) {
    throw new Error('No Spotify connection found');
  }

  const tokenData = tokenDoc.data();

  if (tokenData.expires_at <= Date.now()) {
    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Basic ' + btoa(`${SPOTIFY_CONFIG.CLIENT_ID}:${SPOTIFY_CONFIG.CLIENT_SECRET}`)
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: tokenData.refresh_token
      })
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const newTokenData = await response.json();
    const updatedTokens = {
      ...tokenData,
      access_token: newTokenData.access_token,
      expires_at: Date.now() + (newTokenData.expires_in * 1000)
    };

    const isPremium = await checkSpotifyPremiumStatus(newTokenData.access_token);
    
    await Promise.all([
      setDoc(docRef, updatedTokens),
      updatePremiumStatus(auth.currentUser.uid, isPremium)
    ]);

    return updatedTokens.access_token;
  }

  return tokenData.access_token;
}